import {createPortal} from 'react-dom';
import {MailFileUpload} from './MailFileUpload';
import {useEffect,useState} from 'react';
import {api} from '../api';
import {useStaffSession} from '../ui/StaffSession';
import {withBuildingScope} from '../ui/building-scope';
import {ErrorState} from '../ui/State';
type Document={id:string;title:string;file_name:string;file_size:number};
export type MailboxCapabilities={compose:boolean;approve:boolean;send:boolean};
type Account={id:string;display_name:string;channel_address:string;signature?:string;capabilities:MailboxCapabilities};
type Options={accounts:Account[];documents:Document[]};
export function MailDraftFields({mailbox,fromAccountId,documentIds=[],intakeFiles=[],onCapabilitiesChange,attachmentTarget}:{
 mailbox?:string|undefined;
 fromAccountId?:string|undefined;
 documentIds?:string[]|undefined;
 intakeFiles?:Array<{id:string;name:string}>|undefined;
 onCapabilitiesChange?:(capabilities:MailboxCapabilities|undefined)=>void;
 attachmentTarget?:HTMLElement|null;
}){
 const session=useStaffSession(),building=session?.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const[chosen,setChosen]=useState<string>();
 const[options,setOptions]=useState<Options>(),[error,setError]=useState<unknown>(),[query,setQuery]=useState(''),[selected,setSelected]=useState(documentIds),[known,setKnown]=useState<Document[]>([]);
 useEffect(()=>{
  const controller=new AbortController();
  const timer=setTimeout(()=>{
   void api<Options>(withBuildingScope(`/api/communications/compose-options?q=${encodeURIComponent(query)}`,building),{signal:controller.signal})
    .then(v=>{
     if(controller.signal.aborted)return;
     setOptions(v);
     setKnown(old=>[...new Map([...old,...v.documents].map(d=>[d.id,d])).values()]);
     setError(undefined);
    }).catch(e=>{if(!controller.signal.aborted)setError(e)});
  },query?250:0);
  return()=>{clearTimeout(timer);controller.abort()};
 },[query,building]);
 const initialAccount=fromAccountId??options?.accounts.find(a=>a.channel_address===mailbox)?.id??'';
 useEffect(()=>{onCapabilitiesChange?.(options?.accounts.find(account=>account.id===(chosen??initialAccount))?.capabilities)},[options,initialAccount,chosen,onCapabilitiesChange]);
 const visible=[...new Map([...known.filter(d=>selected.includes(d.id)),...(options?.documents??[])].map(d=>[d.id,d])).values()];
 const attachments=<>
  <MailFileUpload initial={intakeFiles}>
  <details className="mail-attachment-options" open={documentIds.length>0||undefined}>
   <summary>Attach from Documents{selected.length?` · ${selected.length} selected`:''}</summary>
   <fieldset><legend className="sr-only">Repository attachments</legend>
    <label>Find a document<input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search by document title or filename"/></label>
    <small>Recent filed documents appear here. Search by title or filename. Files are checked again before sending; total limit 10 MB.</small>
    {selected.map(id=><input key={id} name="documentIds" type="hidden" value={id}/>)}
    <div className="mail-attachment-picker">{visible.map(d=><label key={d.id}><input type="checkbox" checked={selected.includes(d.id)} onChange={e=>setSelected(old=>e.target.checked?[...old,d.id]:old.filter(id=>id!==d.id))}/><span>{d.title}<small>{d.file_name} · {Math.ceil(d.file_size/1024)} KB</small></span></label>)}</div>
    {query&&!visible.length&&<p>No matching documents.</p>}
    {selected.filter(id=>!known.some(d=>d.id===id)).map(id=><label key={id}><input type="checkbox" checked onChange={()=>setSelected(old=>old.filter(v=>v!==id))}/>Previously selected attachment — retained; remove if no longer needed.</label>)}
   </fieldset>
  </details>
  </MailFileUpload>
  {options?.accounts.find(a=>a.id===(chosen??initialAccount))?.signature&&<button type="button" className="secondary" onClick={event=>{const form=event.currentTarget.closest('form'),body=form?.querySelector<HTMLTextAreaElement>('textarea[name=bodyText]');if(body){body.value+='\n\n'+options.accounts.find(a=>a.id===(chosen??initialAccount))!.signature;body.dispatchEvent(new Event('change',{bubbles:true}));}}}>Insert my signature</button>}
 </>;
 return <>
  <label>From<select aria-label="From" name="fromAccountId" required defaultValue={initialAccount} key={options?.accounts.map(a=>a.id).join(',')} onChange={event=>{setChosen(event.target.value);onCapabilitiesChange?.(options?.accounts.find(account=>account.id===event.target.value)?.capabilities)}}>
   <option value="">Choose an authorised mailbox</option>{options?.accounts.map(a=><option key={a.id} value={a.id}>{a.display_name} · {a.channel_address}</option>)}
  </select></label>
  {options&&!options.accounts.length&&<p>No mailbox permits you to compose. Its administrator can grant access in Mailboxes.</p>}
  {attachmentTarget===undefined?attachments:attachmentTarget?createPortal(attachments,attachmentTarget):null}
  {Boolean(error)&&<ErrorState error={error}/>}
 </>;
}
